import { Injectable } from '@angular/core';
import initSqlJs from 'sql.js';
import {
  BrowserDataCache,
  createBrowserDataCacheAsync,
  createFlowStudioFlowSource,
  createSqliteRuntimeCandidate,
  flowStudioFlowSchema,
  flowStudioFlowTableMapping,
  FlowStudioFlowRow,
  probeSqlJsOpfs,
  removeSqlJsOpfsFiles,
  resolveFlowEnvironmentKey,
  SqlJsOpfsDriverFactory,
} from '@flowstudio/browser-data-cache';
import { StorageRouterService } from './storage-router.service';
import { locateSqliteWasm } from './storage-sqlite.constants';
import { Flow } from '../obj/Flow';

const DEBUG_CACHE_DB_FILE = 'flowstudio-browser-data-cache-debug.sqlite';
const DEBUG_CACHE_PROBE_DB_FILE = 'flowstudio-browser-data-cache-debug-probe.sqlite';
const PRODUCTION_CACHE_DB_FILE = 'flowstudio-flows-v1.sqlite';
const PRODUCTION_CACHE_PROBE_DB_FILE = 'flowstudio-flows-v1-probe.sqlite';

export interface FlowStudioBrowserDataCacheProbeResult {
  environment: string;
  sourceMode: FlowStudioBrowserDataCacheProbeSourceMode;
  runtime: string;
  persistent: boolean;
  sql: boolean;
  rowsWritten: number;
  rowsReturned: number;
  selectedRuntimeId: string;
  runtimeProbeSummary: string;
  sample: Array<{
    flowName?: string;
    displayName?: string;
    environment?: string;
  }>;
}

export interface FlowStudioBrowserDataCacheProductionStatus {
  environment?: string;
  scope?: 'user' | 'admin';
  available: boolean;
  persistent: boolean;
  sql: boolean;
  runtime: string;
  selectedRuntimeId: string;
  runtimeProbeSummary: string;
  rowsReturned?: number;
  fallback: boolean;
  error?: string;
}

export type FlowStudioBrowserDataCacheProbeSourceMode = 'cached' | 'live';

@Injectable({
  providedIn: 'root',
})
export class FlowStudioBrowserDataCacheService {
  private _productionCacheInit: Promise<BrowserDataCache> | null = null;
  private opfsFileRemover = removeSqlJsOpfsFiles;

  constructor(
    private storage: StorageRouterService,
  ) {}

  // ---------------------------------------------------------------------------
  // Production cache
  // ---------------------------------------------------------------------------

  private _getProductionCache(): Promise<BrowserDataCache> {
    if (!this._productionCacheInit) {
      this._productionCacheInit = createBrowserDataCacheAsync({
        sources: [],
        runtimes: [
          createSqliteRuntimeCandidate({
            id: 'main-opfs-sqlite',
            sources: [],
            schema: flowStudioFlowSchema,
            tableMappings: [flowStudioFlowTableMapping],
            driverFactory: new SqlJsOpfsDriverFactory({
              dbFileName: PRODUCTION_CACHE_DB_FILE,
              loadSqlJs: () => initSqlJs({ locateFile: locateSqliteWasm }) as any,
            }),
            probe: () => probeSqlJsOpfs({
              dbFileName: PRODUCTION_CACHE_DB_FILE,
              probeFileName: PRODUCTION_CACHE_PROBE_DB_FILE,
              loadSqlJs: () => initSqlJs({ locateFile: locateSqliteWasm }) as any,
            }),
          }),
        ],
      }).catch((err) => {
        // Reset so a later caller can retry after a transient error.
        this._productionCacheInit = null;
        throw err;
      });
    }
    return this._productionCacheInit;
  }

  private async _getPersistentProductionCache(): Promise<BrowserDataCache | null> {
    try {
      const cache = await this._getProductionCache();
      if (!cache.capabilities().persistent) {
        console.warn(`[FlowStudioBrowserDataCacheService] production package cache selected non-persistent runtime "${cache.selectedRuntimeId}"; using existing storage path`);
        return null;
      }
      return cache;
    } catch (err) {
      console.warn('[FlowStudioBrowserDataCacheService] production package cache unavailable; using existing storage path', err);
      return null;
    }
  }

  private async _getStoredProductionFlows(environment: string, scope: 'user' | 'admin'): Promise<Flow[]> {
    const flows = scope === 'admin'
      ? await this.storage.getFlowsAdmin().catch(() => [] as Flow[])
      : await this.storage.getFlows().catch(() => [] as Flow[]);
    return (flows || []).filter((flow: Flow) => (flow as any).environment === environment);
  }

  private static appFlowToPackageRow(
    flow: Flow,
    environment: string,
    scope: 'user' | 'admin',
  ): FlowStudioFlowRow {
    const envKey = resolveFlowEnvironmentKey(environment, scope);
    const flowName = (flow as any).flowName || (flow as any)._id || '';
    return {
      key: `${envKey}/${flowName}`,
      flowName,
      environment: envKey,
      displayName: (flow as any).displayName || flowName,
      state: (flow as any).flowState || '',
      modifiedAt: (flow.modified instanceof Date
        ? flow.modified.toISOString()
        : (flow.modified as any)) || null,
      rawJson: JSON.stringify(flow),
    };
  }

  private static packageRowToAppFlow(row: FlowStudioFlowRow): Flow {
    try {
      return JSON.parse(row.rawJson) as Flow;
    } catch {
      const f = new Flow();
      (f as any).flowName = row.flowName;
      (f as any).displayName = row.displayName;
      (f as any).flowState = row.state;
      return f;
    }
  }

  async getProductionFlows(environment: string, scope: 'user' | 'admin'): Promise<Flow[]> {
    const cache = await this._getPersistentProductionCache();
    if (!cache) {
      return this._getStoredProductionFlows(environment, scope);
    }

    const envKey = resolveFlowEnvironmentKey(environment, scope);
    try {
      const result = await cache.query<FlowStudioFlowRow>({
        table: 'flows',
        where: { environment: envKey },
      });
      if ((result.rows || []).length > 0) {
        return result.rows.map((row) => FlowStudioBrowserDataCacheService.packageRowToAppFlow(row));
      }
    } catch (err) {
      console.warn('[FlowStudioBrowserDataCacheService] production package cache read failed; using existing storage path', err);
    }

    return this._getStoredProductionFlows(environment, scope);
  }

  async getProductionCacheStatus(
    environment?: string,
    scope: 'user' | 'admin' = 'user',
  ): Promise<FlowStudioBrowserDataCacheProductionStatus> {
    try {
      const cache = await this._getProductionCache();
      const capabilities = cache.capabilities();
      const runtimeProbeSummary = cache.runtimeProbes
        .map((probe) => `${probe.id}:${probe.result.ok ? 'ok' : (probe.result.reason || 'failed')}`)
        .join(',');
      const status: FlowStudioBrowserDataCacheProductionStatus = {
        environment,
        scope,
        available: true,
        persistent: capabilities.persistent,
        sql: capabilities.sql,
        runtime: capabilities.runtime,
        selectedRuntimeId: cache.selectedRuntimeId,
        runtimeProbeSummary,
        fallback: !capabilities.persistent,
      };

      if (environment && capabilities.persistent && capabilities.sql) {
        const envKey = resolveFlowEnvironmentKey(environment, scope);
        const result = await cache.query<FlowStudioFlowRow>({
          table: 'flows',
          where: { environment: envKey },
        });
        status.rowsReturned = result.total ?? result.rows.length;
      }

      return status;
    } catch (err: any) {
      return {
        environment,
        scope,
        available: false,
        persistent: false,
        sql: false,
        runtime: 'unavailable',
        selectedRuntimeId: 'unavailable',
        runtimeProbeSummary: '',
        fallback: true,
        error: String(err?.message ?? err),
      };
    }
  }

  async putProductionFlows(flows: Flow[], environment: string, scope: 'user' | 'admin'): Promise<void> {
    if (!flows.length) return;

    await this.storage.putFlows(flows, scope === 'admin').catch((err) => {
      console.warn('[FlowStudioBrowserDataCacheService] existing storage write failed while mirroring production flows', err);
    });

    const cache = await this._getPersistentProductionCache();
    if (!cache) {
      return;
    }

    const rows = flows.map((f) => FlowStudioBrowserDataCacheService.appFlowToPackageRow(f, environment, scope));
    await cache.upsertRows('flows', rows).catch((err) => {
      console.warn('[FlowStudioBrowserDataCacheService] production package cache write failed', err);
    });
  }

  async resetDebugCache(): Promise<void> {
    await this.opfsFileRemover([
      DEBUG_CACHE_DB_FILE,
      DEBUG_CACHE_PROBE_DB_FILE,
    ]);
  }

  async resetProductionCache(): Promise<void> {
    this._productionCacheInit = null;
    await this.opfsFileRemover([
      PRODUCTION_CACHE_DB_FILE,
      PRODUCTION_CACHE_PROBE_DB_FILE,
    ]);
  }

  async runFlowSummaryProbe(
    environment: string,
    sourceMode: FlowStudioBrowserDataCacheProbeSourceMode = 'cached',
    liveFetch?: (cursor: string | null) => Promise<{ items: any[]; nextCursor: string | null; total?: number }>,
  ): Promise<FlowStudioBrowserDataCacheProbeResult> {
    if (!environment) {
      throw new Error('Browser data cache probe requires an environment.');
    }

    const source = createFlowStudioFlowSource({
      environment,
      listPage: sourceMode === 'live' && liveFetch ? async (cursor?: string | null) => {
        return liveFetch(cursor || null);
      } : async () => {
        const summaries = await this.storage.getFlowSummaries(environment);
        return {
          items: (summaries || []).map((flow: any) => ({
            name: flow.flowName || flow._id,
            environment,
            properties: {
              displayName: flow.displayName || flow.flowName || flow._id,
              state: flow.flowState || '',
              lastModifiedTime: flow.modified instanceof Date
                ? flow.modified.toISOString()
                : flow.modified || null,
            },
          })),
          nextCursor: null,
        };
      },
    });

    const cache = await createBrowserDataCacheAsync({
      sources: [source],
      runtimes: [
        createSqliteRuntimeCandidate({
          id: 'main-opfs-sqlite',
          sources: [source],
          schema: flowStudioFlowSchema,
          tableMappings: [flowStudioFlowTableMapping],
          driverFactory: new SqlJsOpfsDriverFactory({
            dbFileName: DEBUG_CACHE_DB_FILE,
            loadSqlJs: () => initSqlJs({ locateFile: locateSqliteWasm }) as any,
          }),
          probe: () => probeSqlJsOpfs({
            dbFileName: DEBUG_CACHE_DB_FILE,
            probeFileName: DEBUG_CACHE_PROBE_DB_FILE,
            loadSqlJs: () => initSqlJs({ locateFile: locateSqliteWasm }) as any,
          }),
        }),
      ],
    });

    const job = cache.startSync(source.id);
    const sync = await job.done;
    const query = await cache.query<any>({
      table: 'flows',
      where: { environment },
      limit: 5,
    });
    const capabilities = cache.capabilities();
    const runtimeProbeSummary = cache.runtimeProbes
      .map((probe) => `${probe.id}:${probe.result.ok ? 'ok' : (probe.result.reason || 'failed')}`)
      .join(',');

    return {
      environment,
      sourceMode,
      runtime: capabilities.runtime,
      selectedRuntimeId: cache.selectedRuntimeId,
      runtimeProbeSummary,
      persistent: capabilities.persistent,
      sql: capabilities.sql,
      rowsWritten: sync.rowsWritten,
      rowsReturned: query.total ?? query.rows.length,
      sample: query.rows.map((row) => ({
        flowName: row.flowName,
        displayName: row.displayName,
        environment: row.environment,
      })),
    };
  }
}
